import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { IAutomationRecRuleTicketDefDoc } from "./models";
import {
  createTicketAutRecRuleTicketDefDoc,
  getTicketAutRecRuleTicketDefDoc,
  updateTicketAutRecRuleTicketDefDoc,
} from "../../../../../../../services/ticketAutRecRuleTicketDefDoc.service";
import { DefDocInternalProcedures } from "./DefDocInternalProcedures/DefDocInternalProcedures";
import { Insurances } from "./Insurance/Insurances";
import { Agendas } from "./Agendas/Agendas";
import MainApi from "../../../../../../../services/mainApi.service";
import { listProtocolDefault } from "../../../../../../../services/protocolDefault.service";
import {
  AutomationRecRuleTicketDefDocForm,
  automationRecRuleTicketDefDocSchema,
} from "./automationRecRuleTicketDefDocSchema";

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";

const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationRecRuleTicketDefDoc.";

const AutomationRecRuleTicketDefDoc = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
  automationCode,
}: IAutomationRecRuleTicketDefDoc) => {
  const { t } = useTranslation();

  const [options, setOptions] = useState<ISelectType[]>([]);

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutRecRuleTicketDefDoc,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutRecRuleTicketDefDoc,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutRecRuleTicketDefDoc,
  );

  const { sendRequest: requestGeneric } = useFetch(genericService.list);

  const updateHandler = (v: AutomationRecRuleTicketDefDocForm, id: string) =>
    update({
      item: {
        ...v,
        id,
        description: v.description ? v.description : undefined,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
        sendWhenId: v.sendWhen.id,
        ticketDefaultDocId: v.ticketDefaultDoc.id,
        ticketAutRecRuleTicketDefDocsCalendarList:
          v.ticketAutRecRuleTicketDefDocsCalendars
            ? v.ticketAutRecRuleTicketDefDocsCalendars.map((item) => ({
                externalId: item.id,
                externalName: item.name,
              }))
            : undefined,
        ticketAutRecRuleTicketDefDocsInsurancesList:
          v.ticketAutRecRuleTicketDefDocsInsurances
            ? v.ticketAutRecRuleTicketDefDocsInsurances.map((item) => ({
                externalId: item.id,
                externalName: item.name,
              }))
            : undefined,
        ticketAutRecRuleTicketDefDocsInternalProcedureList:
          v.ticketAutRecRuleTicketDefDocsInternalProcedures
            ? v.ticketAutRecRuleTicketDefDocsInternalProcedures.map((item) => ({
                externalId: item.id,
                externalName: item.name,
              }))
            : undefined,
      },
      id: id,
    });

  const saveHandler = (v: AutomationRecRuleTicketDefDocForm) =>
    save({
      ...v,
      description: v.description ? v.description : undefined,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      sendWhenId: v.sendWhen.id,
      ticketDefaultDocId: v.ticketDefaultDoc.id,
      ticketAutRecRuleTicketDefDocsCalendarList:
        v.ticketAutRecRuleTicketDefDocsCalendars
          ? v.ticketAutRecRuleTicketDefDocsCalendars.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            }))
          : undefined,
      ticketAutRecRuleTicketDefDocsInsurancesList:
        v.ticketAutRecRuleTicketDefDocsInsurances
          ? v.ticketAutRecRuleTicketDefDocsInsurances.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            }))
          : undefined,
      ticketAutRecRuleTicketDefDocsInternalProcedureList:
        v.ticketAutRecRuleTicketDefDocsInternalProcedures
          ? v.ticketAutRecRuleTicketDefDocsInternalProcedures.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            }))
          : undefined,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<AutomationRecRuleTicketDefDocForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: AutomationRecRuleTicketDefDocForm = {
          ...data,
          ticketAutRecRuleTicketDefDocsInternalProcedures:
            data.ticketAutRecRuleTicketDefDocsInternalProcedures
              ? data.ticketAutRecRuleTicketDefDocsInternalProcedures.map(
                  (item) => ({
                    id: item.externalId,
                    name: item.externalName,
                  }),
                )
              : [],
          ticketAutRecRuleTicketDefDocsCalendars:
            data.ticketAutRecRuleTicketDefDocsCalendars
              ? data.ticketAutRecRuleTicketDefDocsCalendars.map((item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }))
              : [],
          ticketAutRecRuleTicketDefDocsInsurances:
            data.ticketAutRecRuleTicketDefDocsInsurances
              ? data.ticketAutRecRuleTicketDefDocsInsurances.map((item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }))
              : [],
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const fetchGenerics = useCallback(async () => {
    if (!automationCode) return;
    const { data, success } = await requestGeneric({
      Identifier: "TicketAutomationReceiveProtocolSendWhen",
    });

    if (data && success) {
      setOptions(
        data.map((d) => ({
          id: d.id,
          name: d.name,
        })),
      );
    }
  }, [automationCode, requestGeneric]);

  useEffect(() => {
    fetchGenerics();
  }, [fetchGenerics]);

  const ticketDefaultDocParams = useMemo(
    () => ({ TicketAutomationId: automationCode }),
    [automationCode],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<AutomationRecRuleTicketDefDocForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "ticket_default_doc")}
      schema={automationRecRuleTicketDefDocSchema}
      subtitle={t(translationPath + "new_ticket_default_doc")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item sm={12} textAlign="end">
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={({ name }) => name}
              size="small"
              label={t(translationPath + "send_when")}
              name="sendWhen"
              options={options}
              clearIcon={false}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={({ name }) => name}
              params={ticketDefaultDocParams}
              size="small"
              label={t(translationPath + "ticket_default_doc")}
              name="ticketDefaultDoc"
              request={listProtocolDefault}
              clearIcon={false}
            />
          </Grid>

          {automationCode === "21" && <DefDocInternalProcedures />}

          <Insurances />

          <Agendas automationCode={automationCode} />

          <Grid item xs={12}>
            <FormInput
              size="small"
              multiline
              name="description"
              fullWidth
              minRows={4}
              label={t(translationPath + "description")}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default AutomationRecRuleTicketDefDoc;
